/**
 * 관리자 사용자 관리 (M12) React Query 훅.
 *
 * - 조회: ``useAdminUsers()`` — 60초 staleTime, 화면 진입 시 1회 fetch.
 * - 변이 4종: 생성 / 수정 / 비번 리셋 / (셀프) 비번 변경.
 *   변이 성공 시 사용자 목록 캐시를 무효화하여 최신 상태로 갱신.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createAdminUser,
  listAdminUsers,
  resetAdminUserPassword,
  updateAdminUser,
  type AdminUserCreatePayload,
  type AdminUserOut,
  type AdminUserPatchPayload,
} from "@/lib/api/admin_users";

export const ADMIN_USERS_QUERY_KEY = ["admin", "users"] as const;

export function useAdminUsers() {
  return useQuery<AdminUserOut[]>({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: listAdminUsers,
    staleTime: 60_000,
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserOut, Error, AdminUserCreatePayload>({
    mutationFn: createAdminUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

interface UpdateVars {
  userId: number;
  payload: AdminUserPatchPayload;
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserOut, Error, UpdateVars>({
    mutationFn: ({ userId, payload }) => updateAdminUser(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

interface ResetVars {
  userId: number;
  newPassword: string;
}

export function useResetAdminUserPassword() {
  const qc = useQueryClient();
  return useMutation<AdminUserOut, Error, ResetVars>({
    mutationFn: ({ userId, newPassword }) => resetAdminUserPassword(userId, newPassword),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}
